import React from "react";
import { Image, ImageProps } from "@chakra-ui/react";

// An SVG with nothing in it but a viewBox: it paints no pixels and has the aspect ratio of the file it stands in for.
export function buildEmptyImageWithAspectRatio(width: number, height: number): string {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}'/%3E`;
}

/**
 * A Chakra Image that requests its file (`src`, `srcSet`) only once `shouldLoad` is true, typically from
 * useIsNearViewport. Until then it shows an empty image with the file's aspect ratio (`fileWidth` by
 * `fileHeight`), so its box already has the size the file will give it: an <img> without a src would draw
 * a broken-image icon and its alt text instead. The held-back sources ride along as `data-deferred-src` /
 * `data-deferred-srcset`, which scripts/generate-marketing-static-html.mjs turns back into `src` / `srcset`
 * in the page's <noscript> copy: without JavaScript nothing ever comes near.
 */
export function DeferredImage({
  shouldLoad,
  src,
  srcSet,
  fileWidth,
  fileHeight,
  ...imageProps
}: ImageProps & { shouldLoad: boolean; src: string; fileWidth: number; fileHeight: number }) {
  return (
    <Image
      src={shouldLoad ? src : buildEmptyImageWithAspectRatio(fileWidth, fileHeight)}
      srcSet={shouldLoad ? srcSet : undefined}
      data-deferred-src={shouldLoad ? undefined : src}
      data-deferred-srcset={shouldLoad ? undefined : srcSet}
      {...imageProps}
    />
  );
}
